import { AttributeEntityType, AttributeType, type AttributeDefinition } from "../../clients/Purchaser/Attributes";
import DateUtil from "../../util/DateUtil";
import { FilterChip, type FilterMenuOption } from "../Common/Filters";
import AddressAttributeFilterSubmenu from "./AttributeFilters/AddressAttributeFilterSubmenu";
import DateAttributeFilterSubmenu from "./AttributeFilters/DateAttributeFilterSubmenu";
import FileAttributeFilterSubmenu from "./AttributeFilters/FileAttributeFilterSubmenu";
import NumberAtributeFilterSubmenu from "./AttributeFilters/NumberAttributeFilterSubmenu";
import PhoneAttributeFilterSubmenu from "./AttributeFilters/PhoneAttributeFilterSubmenu";
import PriceAttributeFilterSubmenu from "./AttributeFilters/PriceAttributeFilterSubmenu";
import SelectAttributeFilterSubmenu from "./AttributeFilters/SelectAttributeFilterSubmenu";
import StringAtributeFilterSubmenu from "./AttributeFilters/StringAttributeFilterSubmenu";
import { AttributeTypeIconMap, DateOperatorChipLabel, NumberOperatorChipLabel } from "./shared";
import type { AttributeFilter, SetAttributeFilter } from "./types";

function getAttributeFilterSubmenu(
    entityType: AttributeEntityType,
    attr: AttributeDefinition,
    attributeFilters: AttributeFilter[],
    setAttributeFilter: SetAttributeFilter,
) {
    switch (attr.type) {
        case AttributeType.STRING: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.STRING).find(a => a.attributeId === attr.id);
            return (
                <StringAtributeFilterSubmenu
                    entityType={entityType}
                    attributeId={attr.id}
                    currentFilter={currentFilterValue}
                    setAttributeFilter={setAttributeFilter}
                />
            );
        }
        case AttributeType.FILE: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.FILE).find(a => a.attributeId === attr.id);
            return <FileAttributeFilterSubmenu attributeId={attr.id} currentFilter={currentFilterValue} setAttributeFilter={setAttributeFilter} />;
        }
        case AttributeType.NUMBER: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.NUMBER).find(a => a.attributeId === attr.id);
            return (
                <NumberAtributeFilterSubmenu
                    entityType={entityType}
                    attributeId={attr.id}
                    currentFilter={currentFilterValue}
                    setAttributeFilter={setAttributeFilter}
                />
            );
        }
        case AttributeType.DATE: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.DATE).find(a => a.attributeId === attr.id);
            return (
                <DateAttributeFilterSubmenu
                    attributeId={attr.id}
                    attributeName={attr.name}
                    currentFilter={currentFilterValue}
                    setAttributeFilter={setAttributeFilter}
                />
            );
        }
        case AttributeType.PHONE: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.PHONE).find(a => a.attributeId === attr.id);
            return <PhoneAttributeFilterSubmenu attributeId={attr.id} currentFilter={currentFilterValue} setAttributeFilter={setAttributeFilter} />;
        }
        case AttributeType.SELECT: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.SELECT).find(a => a.attributeId === attr.id);
            return (
                <SelectAttributeFilterSubmenu
                    attributeId={attr.id}
                    type={AttributeType.SELECT}
                    options={attr.options ?? []}
                    currentFilter={currentFilterValue}
                    setAttributeFilter={setAttributeFilter}
                />
            );
        }
        case AttributeType.MULTI_SELECT: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.MULTI_SELECT).find(a => a.attributeId === attr.id);
            return (
                <SelectAttributeFilterSubmenu
                    attributeId={attr.id}
                    type={AttributeType.MULTI_SELECT}
                    options={attr.options ?? []}
                    currentFilter={currentFilterValue}
                    setAttributeFilter={setAttributeFilter}
                />
            );
        }
        case AttributeType.PRICE: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.PRICE).find(a => a.attributeId === attr.id);
            return <PriceAttributeFilterSubmenu attributeId={attr.id} currentFilter={currentFilterValue} setAttributeFilter={setAttributeFilter} />;
        }
        case AttributeType.ADDRESS: {
            const currentFilterValue = attributeFilters.filter(a => a.type === AttributeType.ADDRESS).find(a => a.attributeId === attr.id);
            return <AddressAttributeFilterSubmenu attributeId={attr.id} currentFilter={currentFilterValue} setAttributeFilter={setAttributeFilter} />;
        }
    }
}

export function resolveAttributeFilter(
    entityType: AttributeEntityType,
    attr: AttributeDefinition,
    attributeFilters: AttributeFilter[],
    setAttributeFilter: SetAttributeFilter,
) {
    const Icon = AttributeTypeIconMap[attr.type];
    const currentFilter = attributeFilters.find(f => f.attributeId === attr.id);

    return {
        icon: <Icon className="h-4 w-4 text-stone-500" />,
        title: attr.name,
        activeCount: getActiveValueCount(currentFilter),
        submenu: getAttributeFilterSubmenu(entityType, attr, attributeFilters, setAttributeFilter),
    };
}

// One FilterMenuButton option per attribute, each a submenu of that attribute's filter.
export function attributeFilterOptions(
    entityType: AttributeEntityType,
    attributes: AttributeDefinition[],
    attributeFilters: AttributeFilter[],
    setAttributeFilter: SetAttributeFilter,
): FilterMenuOption[] {
    return attributes.map(attr => {
        const option = resolveAttributeFilter(entityType, attr, attributeFilters, setAttributeFilter);
        return {
            kind: "custom",
            icon: option.icon,
            title: option.title,
            content: option.submenu,
            count: option.activeCount,
            applied: attributeFilters.some(filter => filter.attributeId === attr.id),
        };
    });
}

// How many values a filter has picked, shown on its submenu row. Undefined when nothing is picked, or for
// filter types that pick no countable values.
function getActiveValueCount(filter?: AttributeFilter): number | undefined {
    switch (filter?.type) {
        case AttributeType.STRING:
            return filter.values.length || undefined;
        case AttributeType.DATE:
            return 1;
        case AttributeType.SELECT:
        case AttributeType.MULTI_SELECT:
            return filter.optionIds.length || undefined;
        default:
            return undefined;
    }
}

export function resolveFilterUpdate(newFilter: AttributeFilter, currentFilters: AttributeFilter[]) {
    if (isFilterActive(newFilter)) {
        return [...currentFilters.filter(f => f.attributeId !== newFilter.attributeId), newFilter];
    }
    return currentFilters.filter(f => f.attributeId !== newFilter.attributeId);
}

function isFilterActive(filter: AttributeFilter) {
    switch (filter.type) {
        case AttributeType.STRING:
            return filter.values.length > 0;
        case AttributeType.FILE:
            return filter.set === true || filter.set === false;
        case AttributeType.NUMBER:
            return (
                (filter.operator === "between" && (filter.value || filter.value === 0) && (filter.secondaryValue || filter.secondaryValue === 0)) ||
                filter.value ||
                filter.value === 0
            );
        case AttributeType.DATE:
            return (
                (filter.operator === "between" && (filter.value || filter.value === 0) && (filter.secondaryValue || filter.secondaryValue === 0)) ||
                filter.value ||
                filter.value === 0
            );
        case AttributeType.PHONE:
            return filter.set === true || filter.set === false || (filter.value?.trim().length ?? 0) > 0;
        case AttributeType.SELECT:
            return filter.optionIds.length > 0;
        case AttributeType.MULTI_SELECT:
            return filter.optionIds.length > 0;
        case AttributeType.PRICE:
            return (
                (filter.operator === "between" && (filter.value || filter.value === 0) && (filter.secondaryValue || filter.secondaryValue === 0)) ||
                filter.value ||
                filter.value === 0
            );
        case AttributeType.ADDRESS:
            return filter.set === true || filter.set === false || (filter.value?.trim().length ?? 0) > 0;
    }
}

// `def` lets SELECT / MULTI_SELECT resolve optionIds → labels for the chip.
export function getActiveFilterValueLabel(filter: AttributeFilter, def?: AttributeDefinition) {
    switch (filter.type) {
        case AttributeType.STRING: {
            const valueCount = filter.values?.length ?? 0;
            return valueCount === 1 ? filter.values[0] : `${valueCount} values`;
        }
        case AttributeType.FILE:
            return "";
        case AttributeType.NUMBER: {
            const isBetween = filter.operator === "between";
            const description =
                isBetween && filter.value !== undefined && filter.secondaryValue !== undefined
                    ? `${filter.value} - ${filter.secondaryValue}`
                    : filter.value !== undefined
                      ? String(filter.value)
                      : undefined;
            return description;
        }
        case AttributeType.DATE: {
            const start = filter.value !== undefined ? DateUtil.formatted(filter.value, { format: "M/D/YY" }) : undefined;
            const end = filter.secondaryValue !== undefined ? DateUtil.formatted(filter.secondaryValue, { format: "M/D/YY" }) : undefined;
            if (start && end) return `${start} - ${end}`;
            return start ?? end;
        }
        case AttributeType.PHONE:
            // Set/not-set has no value text; "contains" shows the query.
            return filter.value?.trim() ? filter.value : "";
        case AttributeType.SELECT:
        case AttributeType.MULTI_SELECT: {
            const count = filter.optionIds.length;
            if (count !== 1) return `${count} options`;
            const label = def?.options?.find(o => o.id === filter.optionIds[0])?.value;
            return label ?? `${count} options`;
        }
        case AttributeType.PRICE: {
            const isBetween = filter.operator === "between";
            const description =
                isBetween && filter.value !== undefined && filter.secondaryValue !== undefined
                    ? `${filter.value} - ${filter.secondaryValue}`
                    : filter.value !== undefined
                      ? String(filter.value)
                      : undefined;
            return description;
        }
        case AttributeType.ADDRESS:
            // Set/not-set has no value text; "contains" shows the query.
            return filter.value?.trim() ? filter.value : "";
    }
}

export function getActiveFilterOperator(filter: AttributeFilter) {
    switch (filter.type) {
        case AttributeType.STRING:
            return "is";
        case AttributeType.FILE:
            return filter.set ? "is set" : "is not set";
        case AttributeType.NUMBER:
            return NumberOperatorChipLabel[filter.operator];
        case AttributeType.DATE:
            return DateOperatorChipLabel[filter.operator];
        case AttributeType.PHONE:
            if (filter.value?.trim()) return "contains";
            return filter.set ? "is set" : "is not set";
        case AttributeType.SELECT:
            return filter.optionIds.length > 1 ? "is any of" : "is";
        case AttributeType.MULTI_SELECT:
            return filter.optionIds.length > 1 ? "includes any of" : "includes";
        case AttributeType.PRICE:
            return NumberOperatorChipLabel[filter.operator];
        case AttributeType.ADDRESS:
            if (filter.value?.trim()) return "contains";
            return filter.set ? "is set" : "is not set";
    }
}

export function getActiveFilterHoverText(filter: AttributeFilter, def?: AttributeDefinition) {
    switch (filter.type) {
        case AttributeType.STRING:
            return filter.values.length > 1 ? filter.values.join(", ") : undefined;
        case AttributeType.FILE:
            return undefined;
        case AttributeType.NUMBER:
            return undefined;
        case AttributeType.DATE:
            return undefined;
        case AttributeType.PHONE:
            return undefined;
        case AttributeType.SELECT:
        case AttributeType.MULTI_SELECT: {
            if (filter.optionIds.length <= 1) return undefined;
            const labels = filter.optionIds.map(id => def?.options?.find(o => o.id === id)?.value).filter((v): v is string => !!v);
            return labels.join(", ");
        }
        case AttributeType.PRICE:
            return undefined;
        case AttributeType.ADDRESS:
            return undefined;
    }
}

type AttributeFilterChipProps = {
    filter: AttributeFilter;
    attributeDefinitions: AttributeDefinition[];
    setAttributeFilter: SetAttributeFilter;
};

export function AttributeFilterChip({ filter, attributeDefinitions, setAttributeFilter }: AttributeFilterChipProps) {
    const attr = attributeDefinitions.find(a => a.id === filter.attributeId);
    if (!attr) return null;

    const Icon = AttributeTypeIconMap[filter.type];
    const icon = <Icon className="h-3.5 w-3.5 text-stone-500" />;
    const field = attr.name ?? `Attribute ${filter.attributeId}`;
    const operator = getActiveFilterOperator(filter);
    const description = getActiveFilterValueLabel(filter, attr);
    const hoverText = getActiveFilterHoverText(filter, attr);
    const onClose = () => setAttributeFilter(filter, true);

    return <FilterChip info={{ icon, field, operator, description, hoverText, onClose }} />;
}
